import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { ToastService } from '../../../../../shared/services/toast.service';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import { TicketPipelineStage, TicketStatus } from '../../../models/tickets-config.model';

@Component({
  selector: 'app-tickets-pipeline-stages-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './pipeline-stages-form.component.html'
})
export class TicketsPipelineStagesFormComponent implements OnInit, OnDestroy {
  @Output() saved = new EventEmitter<TicketPipelineStage>();
  @Output() cancelled = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private configApi = inject(TicketsConfigApiService);
  private toast = inject(ToastService);

  isEditing = false;
  itemId: number | null = null;
  /** Pipeline padre (necesario para crear). */
  parentPipelineId: number | null = null;
  saving = false;
  error: string | null = null;
  private originalName = '';

  statuses: TicketStatus[] = [];

  form: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(150)]],
    description: [''],
    color: ['#94a3b8', [Validators.maxLength(30)]],
    sortOrder: [0, [Validators.required]],
    isClosedStage: [false],
    // INC 63: obligatorio — sin mapeo a estado, el kanban y la lista se desincronizan
    // (el ticket se cerraba pero la tarjeta no se movía). El backend responde 400 si falta.
    idTicketStatus: [null, [Validators.required]]
  });

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.configApi.getStatuses().pipe(catchError(() => of([] as TicketStatus[])), takeUntil(this.destroy$))
      .subscribe((s) => {
        this.statuses = s;
        // Los estados pueden llegar después de loadStage() → re-derivar con el valor ya cargado.
        this.aplicarDerivacionEtapaCierre();
      });

    // INC 63: isClosedStage es DERIVADO. Si el estado mapeado es de cierre
    // (isTerminal || isResolved) el backend fuerza isClosedStage=true e ignora lo enviado;
    // reflejarlo en vivo para que la UI no muestre algo distinto a lo que se va a guardar.
    this.form.get('idTicketStatus')!.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.aplicarDerivacionEtapaCierre());
  }

  /** True si el estado es de cierre según los flags del backend (NO por id ni por nombre). */
  private esEstadoDeCierre(idStatus: number | null): boolean {
    if (idStatus == null) return false;
    const s = this.statuses.find((x) => x.id === idStatus);
    return !!s && (s.isTerminal || s.isResolved);
  }

  /** Estado mapeado actualmente seleccionado es de cierre → checkbox forzado y bloqueado. */
  get cierreForzadoPorEstado(): boolean {
    return this.esEstadoDeCierre(this.form.get('idTicketStatus')!.value as number | null);
  }

  /**
   * Marca y deshabilita "etapa de cierre" cuando el estado mapeado es terminal/resuelto,
   * replicando la derivación del backend. Al elegir un estado no-terminal se vuelve a
   * habilitar y el usuario decide.
   */
  private aplicarDerivacionEtapaCierre(): void {
    const ctrl = this.form.get('isClosedStage')!;
    if (this.cierreForzadoPorEstado) {
      if (ctrl.value !== true) ctrl.setValue(true, { emitEvent: false });
      if (ctrl.enabled) ctrl.disable({ emitEvent: false });
    } else if (ctrl.disabled) {
      ctrl.enable({ emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reset(parentPipelineId: number | null = null): void {
    this.isEditing = false;
    this.itemId = null;
    this.parentPipelineId = parentPipelineId;
    this.error = null;
    this.saving = false;
    this.form.get('isClosedStage')!.enable({ emitEvent: false });
    this.form.reset({
      name: '',
      description: '',
      color: '#94a3b8',
      sortOrder: 0,
      isClosedStage: false,
      idTicketStatus: null
    });
  }

  loadStage(id: number): void {
    this.isEditing = true;
    this.itemId = id;
    this.configApi.getPipelineStageById(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.parentPipelineId = item.idPipeline;
        this.form.patchValue({
          name: item.name,
          description: item.description ?? '',
          color: item.color || '#94a3b8',
          sortOrder: item.sortOrder ?? 0,
          isClosedStage: item.isClosedStage,
          idTicketStatus: item.idTicketStatus
        });
        this.originalName = item.name;
        this.aplicarDerivacionEtapaCierre();
      },
      error: (err) => { this.error = err.error?.message || 'Error al cargar la etapa'; }
    });
  }

  hasError(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched));
  }

  guardar(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    if (!this.isEditing && this.parentPipelineId == null) {
      this.error = 'No se identificó el pipeline padre';
      return;
    }
    this.saving = true;
    this.error = null;
    // getRawValue: isClosedStage puede estar deshabilitado (derivado del estado de cierre)
    // y los controles deshabilitados no aparecen en form.value.
    const raw = this.form.getRawValue();
    const req = {
      name: raw.name,
      description: raw.description || undefined,
      color: raw.color || undefined,
      sortOrder: Number(raw.sortOrder ?? 0),
      isClosedStage: !!raw.isClosedStage,
      idTicketStatus: raw.idTicketStatus ?? undefined
    };

    const obs = this.isEditing && this.itemId != null
      ? this.configApi.updatePipelineStage(this.itemId, req)
      : this.configApi.createPipelineStage(this.parentPipelineId!, req);

    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.saving = false;
        // INC 63: isClosedStage lo DERIVA el backend (fuerza true si el estado mapeado es
        // terminal/resuelto, ignorando lo enviado). Repintar con la RESPUESTA, no con lo enviado.
        this.form.get('isClosedStage')!.setValue(item.isClosedStage, { emitEvent: false });
        this.aplicarDerivacionEtapaCierre();
        if (this.isEditing) this.toast.updated('Etapa',
          this.originalName !== (raw.name?.trim() || '')
            ? `Se actualizó "${this.originalName}" → "${raw.name?.trim() || ''}".`
            : `Se actualizó "${raw.name?.trim() || ''}" correctamente.`);
        else this.toast.created('Etapa', `Se creó "${raw.name?.trim() || ''}" correctamente.`);
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        const msg = err.error?.message || 'Error al guardar la etapa';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  cancelar(): void { this.cancelled.emit(); }
}
